import React, { useState, useEffect, useRef } from "react";
import {
    IonPage,
    IonContent,
    IonButton,
    IonCard,
    IonCardContent,
    IonImg,
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define types for questions
type QuestionType = "picture" | "multipleChoice" | "trueFalse";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface TrueFalseQuestion {
    question: string;
    correct: boolean;
}


const TrafficSafetyQuiz: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [showImage, setShowImage] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Initialize the state with an empty object that matches the required structure
    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion)[]>>({
        picture: [],
        multipleChoice: [],
        trueFalse: []
    });

    const fetchQuestions = async () => {
        try {
            let multipleChoiceQuestion: MultipleChoiceQuestion[] = [];
            let trueFalseQuestion: TrueFalseQuestion[] = [];
            let pictureQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesTrueFalse.json');
                trueFalseQuestion = await response.json();

                const response1 = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesMultipleChoiceQuestion.json');
                multipleChoiceQuestion = await response1.json();

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesPicture.json');
                pictureQuestion = await response2.json();

            } else {
                const module = await import('../../../datas/TrafficRulesTrueFalse.json');
                trueFalseQuestion = module.default;

                const module1 = await import('../../../datas/TrafficRulesMultipleChoiceQuestion.json');
                multipleChoiceQuestion = module1.default;

                const module2 = await import('../../../datas/TrafficRulesPicture.json');
                pictureQuestion = module2.default;
            }
            const questionBankList: Record<QuestionType, (PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion)[]> = {
                picture: pictureQuestion,
                multipleChoice: multipleChoiceQuestion,
                trueFalse: trueFalseQuestion,
            };
            setQuestionBank(questionBankList);
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Traffic Rules and Road Safety for UKG Kids | Mama’s Guidance"; // Set page title
    }, []);

    // Use an effect to pick a random question when the question bank is updated
    useEffect(() => {
        if (questionBank.picture.length > 0 || questionBank.multipleChoice.length > 0 || questionBank.trueFalse.length > 0) {
            getRandomQuestion();
        }
    }, [questionBank]); // Trigger when questionBank changes

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        console.log(currentQuestion, option)

        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            // For multiple choice and true/false questions
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const renderQuestion = () => {
        if (!currentQuestion) return <h3>Loading question...</h3>;

        if ("options" in currentQuestion) {
            if (typeof currentQuestion.options[0] === "object") {
                // Picture-based question
                return (
                    <>
                        <h3 className="mb-5">{currentQuestion.question}</h3>
                        <div className="row">
                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                <div
                                    className={`col-12 col-md-3 mb-5 `}
                                    key={index}>
                                    <img
                                        src={process.env.REACT_APP_API_URL + option.src}
                                        onClick={() => handleAnswerSubmit(option.src)}
                                        className={`img-thumbnail ${selectedOption === option.src
                                            ? isCorrect === true
                                                ? "correct"
                                                : "errorImgItems"
                                            : ""
                                            }`}
                                        style={{
                                            cursor: "pointer",
                                            height: '150px',
                                            width: '150px'
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                );
            } else {
                // Multiple-choice question
                return (
                    <>
                        <h3>{currentQuestion.question}</h3>
                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                            {(currentQuestion as MultipleChoiceQuestion).options.map((option, index) => (
                                <IonItem
                                    className={`grid-item ${selectedOption === option
                                        ? isCorrect === true
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonRadio slot="start" value={option} />
                                    <IonLabel>{option}</IonLabel>
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </>
                );
            }
        } else {
            // True/False question
            return (
                <>
                    <h3>{currentQuestion.question}</h3>
                    <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                        <IonItem
                            className={`grid-item ${selectedOption === true
                                ? isCorrect === true
                                    ? "correct"
                                    : "errorItems"
                                : ""
                                }`}>
                            <IonRadio slot="start" value={true} />
                            <IonLabel>True</IonLabel>
                        </IonItem>
                        <IonItem
                            className={`grid-item ${selectedOption === false
                                ? isCorrect === true
                                    ? "correct"
                                    : "errorItems"
                                : ""
                                }`}>
                            <IonRadio slot="start" value={false} />
                            <IonLabel>False</IonLabel>
                        </IonItem>
                    </IonRadioGroup>
                </>
            );
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG students basic traffic rules and road safety with engaging visuals and interactive worksheets. Build awareness and safe habits early on." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/environmental-science/traffic-rules-safety" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Traffic Rules and Road Safety</h1>

                                <p>
                                    Road safety starts young! In this <strong>UKG-level environmental science activity</strong>, children learn the importance of following traffic rules, recognizing safety signs, and practicing safe behavior while walking or crossing the road.
                                </p>

                                <h2>🚦 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Basic traffic lights: red, yellow, and green</li>
                                    <li>Pedestrian rules: using zebra crossing, walking on the footpath</li>
                                    <li>Importance of helmets and seatbelts</li>
                                    <li>Meaning of common road signs and symbols</li>
                                </ul>

                                <h2>🎯 Skills Developed</h2>
                                <ul>
                                    <li>Awareness of public safety</li>
                                    <li>Visual recognition of signs</li>
                                    <li>Understanding rules and consequences</li>
                                    <li>Decision-making and alertness</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/environmental-science/living-non-living-things"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Living & Non-Living Things</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/environmental-science/transport-vehicles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Transport & Vehicles</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 Practical Activities</h2>
                                <ul>
                                    <li>Role-play crossing the street using toy signals</li>
                                    <li>Watch child-safe videos on road safety rules</li>
                                    <li>Take a walk and spot traffic lights and zebra crossings</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Traffic Rules and Road Safety</h1>

                            <p>
                                Road safety starts young! In this <strong>UKG-level environmental science activity</strong>, children learn the importance of following traffic rules, recognizing safety signs, and practicing safe behavior while walking or crossing the road.
                            </p>

                            <h2>🚦 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Basic traffic lights: red, yellow, and green</li>
                                <li>Pedestrian rules: using zebra crossing, walking on the footpath</li>
                                <li>Importance of helmets and seatbelts</li>
                                <li>Meaning of common road signs and symbols</li>
                            </ul>

                            <h2>🎯 Skills Developed</h2>
                            <ul>
                                <li>Awareness of public safety</li>
                                <li>Visual recognition of signs</li>
                                <li>Understanding rules and consequences</li>
                                <li>Decision-making and alertness</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/environmental-science/living-non-living-things"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Living & Non-Living Things</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/environmental-science/transport-vehicles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Transport & Vehicles</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 Practical Activities</h2>
                            <ul>
                                <li>Role-play crossing the street using toy signals</li>
                                <li>Watch child-safe videos on road safety rules</li>
                                <li>Take a walk and spot traffic lights and zebra crossings</li>
                            </ul>
                        </article>
                        {renderQuestion()}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default TrafficSafetyQuiz;
